"use client";

/**
 * fund-sip-chart.tsx
 *
 * SVG dual-line chart showing a ₹10k/month SIP simulation: one line for
 * cumulative amount invested (flat/linear) and one for current value. Users
 * can toggle between 3-year and 5-year windows. Summary metrics row below
 * shows invested total, final value, gain, and XIRR.
 *
 * Used in: src/app/funds/[slug]/page.tsx (Performance Analysis section)
 */

import { useMemo, useState } from "react";
import { formatRupeesShort, formatPctDirect } from "@/lib/utils/format";

export interface SipMonthlyPoint {
  month: string; // "YYYY-MM"
  invested: number;
  value: number;
}

export interface FundSipChartProps {
  threeYearMonthly: SipMonthlyPoint[] | null;
  fiveYearMonthly: SipMonthlyPoint[] | null;
  threeYearXirrPct: number | null;
  fiveYearXirrPct: number | null;
}

const WIDTH = 640;
const HEIGHT = 260;
const PAD_LEFT = 50;
const PAD_RIGHT = 16;
const PAD_TOP = 16;
const PAD_BOTTOM = 32;

function buildPath(
  points: Array<{ x: number; y: number }>
): string {
  if (points.length === 0) return "";
  return points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(" ");
}

export function FundSipChart({
  threeYearMonthly,
  fiveYearMonthly,
  threeYearXirrPct,
  fiveYearXirrPct,
}: FundSipChartProps) {
  const hasThree = !!threeYearMonthly && threeYearMonthly.length > 1;
  const hasFive = !!fiveYearMonthly && fiveYearMonthly.length > 1;
  const defaultWindow: "3Y" | "5Y" = hasFive ? "5Y" : hasThree ? "3Y" : "3Y";
  const [window, setWindow] = useState<"3Y" | "5Y">(defaultWindow);

  const data = window === "3Y" ? threeYearMonthly : fiveYearMonthly;
  const xirrPct = window === "3Y" ? threeYearXirrPct : fiveYearXirrPct;

  const computed = useMemo(() => {
    if (!data || data.length < 2) return null;

    const chartW = WIDTH - PAD_LEFT - PAD_RIGHT;
    const chartH = HEIGHT - PAD_TOP - PAD_BOTTOM;

    const maxVal = Math.max(
      ...data.map((d) => Math.max(d.invested, d.value))
    );
    const minVal = 0;
    const range = maxVal - minVal || 1;

    const xFor = (i: number) =>
      PAD_LEFT + (i / (data.length - 1)) * chartW;
    const yFor = (v: number) =>
      PAD_TOP + chartH - ((v - minVal) / range) * chartH;

    const investedPoints = data.map((d, i) => ({
      x: xFor(i),
      y: yFor(d.invested),
    }));
    const valuePoints = data.map((d, i) => ({
      x: xFor(i),
      y: yFor(d.value),
    }));

    // Y-axis labels (4 ticks)
    const ticks = [0, 0.33, 0.66, 1].map((t) => {
      const v = minVal + t * range;
      return { y: yFor(v), label: formatRupeesShort(v) };
    });

    // Area path for value (fill under value line above invested)
    const last = data[data.length - 1];
    const first = data[0];

    return {
      investedPath: buildPath(investedPoints),
      valuePath: buildPath(valuePoints),
      ticks,
      first,
      last,
    };
  }, [data]);

  if (!hasThree && !hasFive) {
    return (
      <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
        <p className="text-muted-foreground text-sm">
          Insufficient NAV history for SIP simulation.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
      {/* Header + toggle */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-foreground text-sm font-serif">
            ₹10,000/month SIP simulation
          </div>
          <div className="text-dim text-[11px]">
            Invested vs current value over time
          </div>
        </div>
        <div className="inline-flex bg-surface-light/10 rounded-lg p-0.5">
          <button
            type="button"
            onClick={() => setWindow("3Y")}
            disabled={!hasThree}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
              window === "3Y"
                ? "bg-gold text-black"
                : "text-muted-foreground hover:text-foreground"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            3 Years
          </button>
          <button
            type="button"
            onClick={() => setWindow("5Y")}
            disabled={!hasFive}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
              window === "5Y"
                ? "bg-gold text-black"
                : "text-muted-foreground hover:text-foreground"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            5 Years
          </button>
        </div>
      </div>

      {/* Chart */}
      {computed ? (
        <>
          <div className="w-full overflow-x-auto">
            <svg
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              className="w-full h-auto min-w-[480px]"
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Gridlines + Y labels */}
              {computed.ticks.map((t, i) => (
                <g key={i}>
                  <line
                    x1={PAD_LEFT}
                    x2={WIDTH - PAD_RIGHT}
                    y1={t.y}
                    y2={t.y}
                    stroke="rgba(148,163,184,0.12)"
                    strokeWidth={1}
                  />
                  <text
                    x={PAD_LEFT - 8}
                    y={t.y + 4}
                    textAnchor="end"
                    fontSize="10"
                    fill="#64748B"
                  >
                    {t.label}
                  </text>
                </g>
              ))}

              {/* Invested line (flat dashed gray) */}
              <path
                d={computed.investedPath}
                fill="none"
                stroke="#64748B"
                strokeWidth={2}
                strokeDasharray="4 4"
              />

              {/* Value line (green solid) */}
              <path
                d={computed.valuePath}
                fill="none"
                stroke="#10B981"
                strokeWidth={2.5}
                strokeLinejoin="round"
                strokeLinecap="round"
              />

              {/* X-axis labels: start and end month */}
              <text
                x={PAD_LEFT}
                y={HEIGHT - 10}
                fontSize="10"
                fill="#64748B"
                textAnchor="start"
              >
                {computed.first.month}
              </text>
              <text
                x={WIDTH - PAD_RIGHT}
                y={HEIGHT - 10}
                fontSize="10"
                fill="#64748B"
                textAnchor="end"
              >
                {computed.last.month}
              </text>
            </svg>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-2 text-[11px]">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <span
                className="inline-block w-4 h-0.5"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, #64748B 50%, transparent 50%)",
                  backgroundSize: "6px 2px",
                }}
              />
              Invested
            </span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <span className="inline-block w-4 h-0.5 bg-cgreen" />
              Current value
            </span>
          </div>

          {/* Summary metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            <div>
              <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-1">
                Invested
              </div>
              <div className="text-foreground text-sm font-bold font-tabular">
                {formatRupeesShort(computed.last.invested)}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-1">
                Final value
              </div>
              <div className="text-foreground text-sm font-bold font-tabular">
                {formatRupeesShort(computed.last.value)}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-1">
                Gain
              </div>
              <div className="text-cgreen text-sm font-bold font-tabular">
                {formatRupeesShort(
                  Math.max(0, computed.last.value - computed.last.invested)
                )}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-1">
                XIRR
              </div>
              <div className="text-cgreen text-sm font-bold font-tabular">
                {xirrPct !== null && xirrPct !== undefined
                  ? formatPctDirect(xirrPct)
                  : "-"}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="text-muted-foreground text-sm">
          Data not available for this window.
        </p>
      )}
    </div>
  );
}
